import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import React, { useContext, useEffect } from 'react';
import classnames from 'classnames';
import ddt from '@wix/da-ddt';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useCommentPostAction } from '../../../CommentEditor/hooks/useCommentPostAction';
import { useCommentCancelAction } from '../../../CommentEditor/hooks/useCommentCancelAction';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import LoadingPlaceholder from '../LoadingPlaceholder';
import LazyPlaceholder from '../LazyPlaceholder';
import CommentBoxFooter from '../CommentBoxFooter';
import { CommentEditorContext, EditorContext } from '../../../CommentEditor/context';
import CommentEditor from '../../../CommentEditor';
import s from './CommentBoxEditor.scss';
/** The core of our comment box. This is a `<CommentEditor />` with a footer and a loading indicator */ const CommentBoxEditor = (_param)=>{
    var { className, footerClassName, nc, onCancel, onPost } = _param, restProps = _object_without_properties(_param, [
        "className",
        "footerClassName",
        "nc",
        "onCancel",
        "onPost"
    ]);
    const isMounted = useIsMounted();
    const { editorId, isBusy, commentableItemId, commentableType, shouldUseFallback, isExpanded, isFocusRequested } = useContext(CommentEditorContext);
    const { isLoadingTriggered } = useContext(LoadableComponentContext);
    const { api } = useContext(EditorContext);
    useEffect(()=>{
        if (!api) {
            return;
        }
        if (isFocusRequested) {
            ddt.log('comments', 'Focusing CommentBox editor in InView.onChange, id: ', editorId);
            api.focusEditor();
        }
    }, [
        isFocusRequested,
        api,
        editorId
    ]);
    const postComment = useCommentPostAction({
        messageId: nc === null || nc === void 0 ? void 0 : nc.messageId,
        folderId: nc === null || nc === void 0 ? void 0 : nc.folderId,
        shouldDeleteMessage: nc === null || nc === void 0 ? void 0 : nc.shouldDeleteMessage,
        onPost
    });
    const cancelComment = useCommentCancelAction({
        onCancel
    });
    return /*#__PURE__*/ React.createElement("div", {
        /*
        This key is necessary to get the fallback editor working for older safari versions.

        The short of it is that old safari is weird and doesn't force a rerender when `shouldUseFallback` changes on the frontend.

        Now why did this take so long to fix and why did I use `isMounted` instead of `shouldUseFallback` as a key? I tried basing the key on `shouldUseFallback` (which definitely changes between SSR and clientside rendering),
        but that didn't work for some unknown reason. I still haven't figured that one out. So -- under the assumption this wasn't a hydration issue -- I went on a wild goose chase of:
          1. Verifying that the css we're importing contained the correct class names -- it did
          2. Verifying `classnames` was returning the correct value -- it was
          3. Verifying that overlapping CSS class names weren't causing issues (i.e. `.expanded` being used on both the editor and the wrapping element) - they weren't
          4. Triple checking that `shouldUseFallback` was DEFINITELY being updated on the client in old safari -- it is
          5. Checking the entire component tree to make sure a weird React.memo wasn't causing issues -- no memos to be seen.
          X. So many other things I won't bother listing.

        But in a fit of desperation I tried a different client-side-updating variable to reference in the key and behold! It works.
      */ key: // Guard against rerendering outside of fallback contexts as well by checking `shouldUseFallback` before
        // passing a key in
        shouldUseFallback ? `safari-force-rerender-key-${isMounted}` : undefined,
        className: classnames(s['root'], (isExpanded || shouldUseFallback) && s['expanded'], shouldUseFallback && s['fallback'], className)
    }, isBusy && /*#__PURE__*/ React.createElement(LoadingIndicator, {
        className: s['loading']
    }), !isLoadingTriggered && !shouldUseFallback && /*#__PURE__*/ React.createElement(LazyPlaceholder, null), /*#__PURE__*/ React.createElement(CommentEditor, _object_spread({
        className: classnames(s['commentbox-editor'], (isExpanded || shouldUseFallback) && s['expanded']),
        loadingIndicator: /*#__PURE__*/ React.createElement(LoadingPlaceholder, null),
        onKeyboardSubmit: postComment
    }, restProps)), (shouldUseFallback || isLoadingTriggered) && /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            itemid: typeof commentableItemId === 'string' ? parseInt(commentableItemId) : commentableItemId,
            typeid: commentableType
        }
    }, /*#__PURE__*/ React.createElement(CommentBoxFooter, {
        onCancel: cancelComment,
        onPost: postComment,
        className: classnames(s['commentbox-footer'], footerClassName)
    })), nc && nc.postfix);
};
CommentBoxEditor.displayName = 'CommentBoxEditor';
export default CommentBoxEditor;

//# sourceMappingURL=CommentBoxEditor.js.map